import { useMutation } from "@tanstack/react-query";
import { Button } from "components/Clickable";
import { TextLink } from "components/Clickable/Links";
import { TextArea } from "components/Input/Input";
import { Modal } from "components/Modal";
import { writeCMSDataClientSide } from "helpers/api";
import { useRouter } from "next/router";
import React, { useContext } from "react";
import { Pressable, VisuallyHidden } from "react-aria";
import { DialogTrigger, Heading } from "react-aria-components";

interface Context {
    showLink: boolean;
    ruleset: number;
}
const context = React.createContext<Context>({ showLink: false, ruleset: 1 });

export function FeedbackContextProvider({
    children,
    ...props
}: React.PropsWithChildren<Context>) {
    return <context.Provider value={props}>{children}</context.Provider>;
}

export default function VisualizerFeedbackForm() {
    const router = useRouter();
    const { showLink, ruleset } = useContext(context);
    const mutation = useMutation(
        async (comment: string) => {
            const response = await writeCMSDataClientSide(
                "POST",
                "/api/submit_visualizer_feedback/",
                {},
                {
                    ruleset,
                    comment,
                    configured_rules: Object.entries(router.query).flatMap(
                        ([key, value]) => {
                            const rulematch = key.match(/^rule-(\d+)$/);
                            if (rulematch?.at(1)) {
                                return {
                                    rule: rulematch.at(1),
                                    option: value,
                                };
                            } else return [];
                        },
                    ),
                },
            );
            if (!response.ok)
                throw new Error(
                    `Feedback modal received error: ${response.status} ${response.statusText}`,
                );
        },
        { mutationKey: ["visualizer-feedback", router.query] },
    );
    if (!showLink) return null;
    return (
        <DialogTrigger>
            <Pressable>
                <TextLink>Image look wrong? Let us know!</TextLink>
            </Pressable>
            <Modal>
                <Heading level={2}>What seems to be the problem?</Heading>
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        mutation.mutate(e.currentTarget.comment.value);
                    }}
                >
                    {mutation.isIdle ? (
                        <>
                            <label htmlFor="visualizer_feedback_comment">
                                <VisuallyHidden>
                                    Tell us what's wrong
                                </VisuallyHidden>
                            </label>
                            <TextArea
                                name="comment"
                                id="visualizer_feedback_comment"
                            />
                            <Button type="submit">submit</Button>
                        </>
                    ) : mutation.isLoading ? (
                        <p>submitting...</p>
                    ) : mutation.isSuccess ? (
                        <p>
                            Success! Thanks for your feedback!{" "}
                            <TextLink onClick={mutation.reset}>🔄</TextLink>
                        </p>
                    ) : (
                        <p>
                            well crap, that didn't work{" "}
                            <TextLink onClick={mutation.reset}>🔄</TextLink>
                        </p>
                    )}
                </form>
            </Modal>
        </DialogTrigger>
    );
}
